/**
 * The people in the group.
 *
 * The screen is arranged around one job: turning a guest into a player. A guest
 * is a name someone typed at the table to get a rating in (decision 6); it sits
 * outside the roster until somebody saves it. That is the only irreversible-
 * feeling step here, so it gets the top of the screen and a real button rather
 * than a menu item — and it says, before you press it, that nothing already
 * rated is lost.
 *
 * Everything else is deliberately plain. Rules live in `logic.ts`; this file
 * only decides what is on screen and in what order.
 */

import { useEffect, useState } from "react";
import type { ReactNode } from "react";

import type { ScreenProps } from "../../main.tsx";
import type { DepthMode, Profile } from "../../../shared/types.ts";
import { createRow, tombstone, useProfiles, useUsage, write } from "./data.ts";
import {
  DEPTH_COPY,
  PROFILE_COLOURS,
  archivePatch,
  disposalOptions,
  draftFromProfile,
  isArchived,
  isGuest,
  newProfileDraft,
  profilePatchFrom,
  profileProblems,
  promoteGuestPatch,
  promotionDraft,
  promotionProblem,
  promotionWarning,
  restorePatch,
} from "./logic.ts";
import type { ProfileDraft } from "./logic.ts";
import {
  Banner,
  ConfirmButton,
  Empty,
  Note,
  Problems,
  Section,
  Segmented,
  SettingsScreen,
  TextField,
  Dot,
} from "./ui.tsx";

const DEPTH_OPTIONS = (["FULL", "SIMPLE", "KID"] as const).map((value) => ({
  value: value as DepthMode,
  copy: DEPTH_COPY[value],
}));

type EditorMode = "create" | "edit" | "promote";

interface EditorState {
  mode: EditorMode;
  /** The row being edited or promoted; null while creating. */
  id: string | null;
  draft: ProfileDraft;
}

function colourLabel(colour: string): string {
  const match = PROFILE_COLOURS.find((c) => c.hex.toUpperCase() === colour.trim().toUpperCase());
  return match ? match.name : "Custom colour";
}

/** Blank avatars are stored as null, not as an empty string, so both devices agree. */
function cleaned(draft: ProfileDraft): ProfileDraft {
  const avatar = draft.avatar?.trim();
  return { ...draft, name: draft.name.trim(), avatar: avatar ? avatar : null };
}

export function Profiles(props: ScreenProps): ReactNode {
  const { rows, loading, error } = useProfiles();
  const usage = useUsage("session_participant", "profile_id");

  const [editor, setEditor] = useState<EditorState | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  // A session that ends with an unsaved guest links straight here.
  const [promoteRequest, setPromoteRequest] = useState<string | null>(
    () => props.query.get("promote"),
  );

  const sessionsOf = (id: string): number => usage.get(id) ?? 0;

  /** Most-played first (decision 22), with the count on screen so the order reads. */
  const byUse = (list: readonly Profile[]): Profile[] =>
    [...list].sort((a, b) => sessionsOf(b.id) - sessionsOf(a.id) || a.name.localeCompare(b.name));

  const guests = byUse(rows.filter((p) => isGuest(p) && !isArchived(p)));
  const saved = byUse(rows.filter((p) => !isGuest(p) && !isArchived(p)));
  const archived = [...rows.filter(isArchived)].sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    if (promoteRequest === null) return;
    const guest = rows.find((p) => p.id === promoteRequest);
    if (!guest) return; // Rows arrive asynchronously; wait rather than clearing the request.
    setPromoteRequest(null);
    if (isGuest(guest)) {
      setEditor({ mode: "promote", id: guest.id, draft: promotionDraft(guest, rows) });
    }
  }, [promoteRequest, rows]);

  async function run(id: string, action: () => Promise<unknown>): Promise<void> {
    setBusyId(id);
    setFailure(null);
    try {
      await action();
      setEditor(null);
    } catch (err) {
      setFailure(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  function editorFor(id: string | null, mode: EditorMode): EditorState | null {
    return editor && editor.mode === mode && editor.id === id ? editor : null;
  }

  function setDraft(draft: ProfileDraft): void {
    setEditor((current) => (current ? { ...current, draft } : current));
  }

  function submit(): void {
    if (!editor) return;
    const draft = cleaned(editor.draft);
    const target = editor.id === null ? null : rows.find((p) => p.id === editor.id);

    if (editor.mode === "create") {
      void run("new", () => createRow("profile", profilePatchFrom(draft)));
      return;
    }
    if (!target) return;
    // `promoteGuestPatch` throws if the promotion is not allowed; building the
    // patch inside the action puts that on the same error path as a failed write.
    void run(target.id, async () => {
      const patch =
        editor.mode === "promote" ? promoteGuestPatch(target, draft) : profilePatchFrom(draft);
      return write("profile", target.id, patch, target.version);
    });
  }

  const creating = editorFor(null, "create");

  return (
    <SettingsScreen
      title="Players"
      subtitle="Everyone who rates. One row each — nobody needs an account."
      back={{ to: "/settings", label: "Settings" }}
    >
      {error !== null && (
        <Banner tone="danger" title="Cannot read the players">
          {error}
        </Banner>
      )}
      {failure !== null && (
        <Banner tone="danger" title="That change did not save">
          {failure}
        </Banner>
      )}

      {guests.length > 0 && (
        <Section
          title="Guests from the table"
          hint="Someone typed these names mid-session to get a rating in. They stay out of the roster until you save them."
        >
          <Note>
            Saving a guest keeps everything they already rated — the row keeps its id, so their
            existing ratings become part of the new player's history.
          </Note>
          <ul className="pb-list">
            {guests.map((guest) => {
              const open = editorFor(guest.id, "promote");
              const problem = open ? promotionProblem(guest, cleaned(open.draft)) : null;
              const warning = open ? promotionWarning(cleaned(open.draft), rows) : null;
              return (
                <li key={guest.id} className="pb-tile" data-edge="ochre">
                  <div className="pb-stack">
                    <div className="pb-item">
                      <Dot colour={guest.colour} title={colourLabel(guest.colour)} />
                      <span className="pb-item__main">
                        <span className="pb-item__name">
                          {guest.avatar !== null && <span aria-hidden="true">{guest.avatar}</span>}
                          {guest.name || "Unnamed guest"}
                          <span className="pb-tag" data-tone="guest">
                            Guest
                          </span>
                        </span>
                        <span className="pb-item__meta">
                          {sessionsOf(guest.id)} session{sessionsOf(guest.id) === 1 ? "" : "s"}{" "}
                          rated
                        </span>
                      </span>
                    </div>

                    {open === null ? (
                      <div className="pb-actions">
                        <button
                          type="button"
                          className="pb-btn"
                          data-variant="primary"
                          onClick={() =>
                            setEditor({
                              mode: "promote",
                              id: guest.id,
                              draft: promotionDraft(guest, rows),
                            })
                          }
                        >
                          Save as a player
                        </button>
                        <RowActions
                          profile={guest}
                          sessions={sessionsOf(guest.id)}
                          busy={busyId === guest.id}
                          onRun={run}
                        />
                      </div>
                    ) : (
                      <>
                        {warning !== null && (
                          <Banner tone="warn" title="Same name as someone saved">
                            {warning}
                          </Banner>
                        )}
                        <Editor
                          draft={open.draft}
                          onDraft={setDraft}
                          problems={problem === null ? [] : [problem]}
                          submitLabel="Save as a player"
                          busy={busyId === guest.id}
                          onSubmit={submit}
                          onCancel={() => setEditor(null)}
                        />
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </Section>
      )}

      <Section
        title="Saved players"
        aside={
          creating === null ? (
            <button
              type="button"
              className="pb-btn"
              data-variant="quiet"
              onClick={() => setEditor({ mode: "create", id: null, draft: newProfileDraft(rows) })}
            >
              New player
            </button>
          ) : null
        }
        hint="Most-played first."
      >
        {creating !== null && (
          <div className="pb-tile" data-edge="spice">
            <Editor
              draft={creating.draft}
              onDraft={setDraft}
              problems={profileProblems(cleaned(creating.draft))}
              submitLabel="Add player"
              busy={busyId === "new"}
              onSubmit={submit}
              onCancel={() => setEditor(null)}
            />
          </div>
        )}

        {loading ? (
          <Empty>Reading the roster…</Empty>
        ) : saved.length === 0 ? (
          <Empty>
            Nobody saved yet. Add the people you play with, or save a guest from the table.
          </Empty>
        ) : (
          <ul className="pb-list">
            {saved.map((profile) => {
              const open = editorFor(profile.id, "edit");
              const sessions = sessionsOf(profile.id);
              if (open !== null) {
                return (
                  <li key={profile.id} className="pb-tile" data-edge="slate">
                    <Editor
                      draft={open.draft}
                      onDraft={setDraft}
                      problems={profileProblems(cleaned(open.draft))}
                      submitLabel="Save"
                      busy={busyId === profile.id}
                      onSubmit={submit}
                      onCancel={() => setEditor(null)}
                      footer={
                        <RowActions
                          profile={profile}
                          sessions={sessions}
                          busy={busyId === profile.id}
                          onRun={run}
                        />
                      }
                    />
                  </li>
                );
              }
              return (
                <li key={profile.id} className="pb-item">
                  <Dot colour={profile.colour} title={colourLabel(profile.colour)} />
                  <span className="pb-item__main">
                    <span className="pb-item__name">
                      {profile.avatar !== null && <span aria-hidden="true">{profile.avatar}</span>}
                      {profile.name}
                    </span>
                    <span className="pb-item__meta">
                      {sessions} session{sessions === 1 ? "" : "s"} · {DEPTH_COPY[profile.depth_mode].title}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="pb-iconbtn"
                    aria-label={`Edit ${profile.name}`}
                    onClick={() =>
                      setEditor({ mode: "edit", id: profile.id, draft: draftFromProfile(profile) })
                    }
                  >
                    ✎
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      {archived.length > 0 && (
        <Section
          title="Archived"
          hint="Out of the pickers, still in the record. Every session they rated keeps their name and colour."
        >
          <ul className="pb-list">
            {archived.map((profile) => {
              const sessions = sessionsOf(profile.id);
              return (
                <li key={profile.id} className="pb-item" data-archived="true">
                  <Dot colour={profile.colour} title={colourLabel(profile.colour)} />
                  <span className="pb-item__main">
                    <span className="pb-item__name">{profile.name}</span>
                    <span className="pb-item__meta">
                      {sessions} session{sessions === 1 ? "" : "s"}
                    </span>
                  </span>
                  <RowActions
                    profile={profile}
                    sessions={sessions}
                    busy={busyId === profile.id}
                    onRun={run}
                  />
                </li>
              );
            })}
          </ul>
        </Section>
      )}
    </SettingsScreen>
  );
}

/**
 * Name, colour, avatar, depth — the same four fields whether the row is new,
 * being edited, or being promoted out of guest status. Promotion showing the
 * identical form is the point: it is not a different kind of person afterwards.
 */
function Editor(props: {
  draft: ProfileDraft;
  onDraft: (draft: ProfileDraft) => void;
  problems: readonly string[];
  submitLabel: string;
  busy: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  footer?: ReactNode;
}): ReactNode {
  const { draft, onDraft } = props;
  const blocked = props.problems.length > 0;

  return (
    <div className="pb-stack">
      <TextField
        label="Name"
        value={draft.name}
        onChange={(name) => onDraft({ ...draft, name })}
        placeholder="Lea"
        autoFocus
        invalid={draft.name.trim() === ""}
      />

      <div className="pb-field">
        <span className="pb-field__label">Colour</span>
        <div className="pb-swatches" role="group" aria-label="Colour">
          {PROFILE_COLOURS.map((colour) => (
            <button
              key={colour.hex}
              type="button"
              className="pb-swatch-picker"
              // The stored hex is data, not a theme decision: this dot has to be
              // the same person in both themes and inside an exported backup.
              style={{ background: colour.hex }}
              aria-label={colour.name}
              aria-pressed={colour.hex.toUpperCase() === draft.colour.trim().toUpperCase()}
              onClick={() => onDraft({ ...draft, colour: colour.hex })}
            />
          ))}
        </div>
        <span className="pb-row__hint">
          Picked: {colourLabel(draft.colour)}. The name is always shown beside the dot, so nobody
          has to tell two colours apart.
        </span>
      </div>

      <TextField
        label="Avatar"
        value={draft.avatar ?? ""}
        onChange={(avatar) => onDraft({ ...draft, avatar: avatar === "" ? null : avatar })}
        placeholder="🦊"
        hint="An emoji, or one or two initials. Nothing to upload, and it is fine to leave empty."
      />

      <Segmented
        label="Depth"
        value={draft.depth_mode}
        options={DEPTH_OPTIONS}
        onChange={(depth_mode) => onDraft({ ...draft, depth_mode })}
      />

      <Problems items={props.problems} />

      <div className="pb-actions">
        <button
          type="button"
          className="pb-btn"
          data-variant="primary"
          disabled={blocked || props.busy}
          onClick={props.onSubmit}
        >
          {props.busy ? "Saving…" : props.submitLabel}
        </button>
        <button type="button" className="pb-btn" data-variant="quiet" onClick={props.onCancel}>
          Cancel
        </button>
      </div>
      {props.footer}
    </div>
  );
}

/**
 * Archive is always on offer; permanent deletion only for a row nothing in the
 * record points at. The session count is shown either way, because it is the
 * honest reason the delete is refused.
 */
function RowActions(props: {
  profile: Profile;
  sessions: number;
  busy: boolean;
  onRun: (id: string, action: () => Promise<unknown>) => Promise<void>;
}): ReactNode {
  const { profile, sessions, busy, onRun } = props;
  const disposal = disposalOptions({ sessions });
  const archived = isArchived(profile);

  return (
    <div className="pb-actions">
      <button
        type="button"
        className="pb-btn"
        data-variant="quiet"
        disabled={busy}
        onClick={() =>
          void onRun(profile.id, () =>
            write(
              "profile",
              profile.id,
              archived ? restorePatch() : archivePatch(),
              profile.version,
            ),
          )
        }
      >
        {archived ? "Restore" : "Archive"}
      </button>
      <ConfirmButton
        label="Delete"
        variant="danger"
        title={`Delete ${profile.name} for good?`}
        consequences={[
          `${profile.name || "This player"} is removed here and on every device you sync.`,
          "No session and no rating refers to them, so nothing else changes.",
          "This cannot be undone. Archiving can.",
        ]}
        confirmLabel="Delete permanently"
        disabled={!disposal.destroy.allowed || busy}
        disabledReason={disposal.destroy.blocked_because}
        onConfirm={() => onRun(profile.id, () => tombstone("profile", profile.id, profile.version))}
      />
    </div>
  );
}
